<template>
  <div class="rd-screen">
    <report-tools :copy="hasRows" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!hasRows" icon="mdi-chart-bar" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      @sort="sortBy"
    ></ui-table>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabRestaurantSummary from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabRestaurantSummary.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Pregled po restoranima firme "više restorana" na računaru: po restoranu broj obroka, ukupan iznos i koliko je
 * platila firma, a koliko radnik (kad firma ima više poslovnica, poseban red za svaku poslovnicu restorana). Restorane,
 * poslovnice i period čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel
 * iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiRestaurantSummary",
  extends: CompanyMultiRestaurantTabRestaurantSummary,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    busy() {
      return this.loading;
    },
    /** Ima podataka kad je bar jedan restoran imao obroke ili iznos (stari tab navodi i restorane sa nulama). */
    hasRows() {
      return this.rows.some((row) => row.meals || row.amount);
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      const suffix = this.currencySuffix;
      const money = { align: "end", num: true, zero: true, sortable: true, format: this.money };
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("restaurant"), name: true, sortable: true },
        ...(this.hasBranches
          ? [{ key: "branch", label: this.$t("branch"), muted: true, sortable: true, value: (row) => row.branch || "–", title: (row) => row.branch }]
          : []),
        { key: "meals", label: this.$t("meal_count"), align: "end", num: true, zero: true, sortable: true },
        { key: "amount", label: `${this.$t("amount")}${suffix}`, ...money },
        { key: "company", label: `${this.$t("company_amount")}${suffix}`, ...money },
        { key: "employee", label: `${this.$t("employee_amount")}${suffix}`, ...money },
      ];
    },
    /** Restorani bez zbirnog reda starog taba. */
    rows() {
      return this.sortRows(
        this.restaurantSummary
          .filter((item) => !item.isTotal)
          .map((item) => ({
            key: String(item.id),
            name: this.toLatin(item.restaurant || ""),
            branch: item.company_name || "",
            meals: item.meal_count,
            amount: item.total_amount,
            company: item.company_amount,
            employee: item.employee_amount,
          }))
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          meals: sums.meals + row.meals,
          amount: sums.amount + row.amount,
          company: sums.company + row.company,
          employee: sums.employee + row.employee,
        }),
        { meals: 0, amount: 0, company: 0, employee: 0 }
      );
    },
    kpis() {
      return [
        { key: "meals", label: this.$t("meal_count"), value: this.sums.meals, tone: "inverse" },
        { key: "amount", label: this.$t("amount"), value: this.money(this.sums.amount) },
        { key: "company", label: this.$t("company_amount"), value: this.money(this.sums.company) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = [
        this.$t("restaurant"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        this.$t("meal_count"),
        this.$t("amount"),
        this.$t("company_amount"),
        this.$t("employee_amount"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...(this.hasBranches ? [row.branch] : []),
        row.meals,
        this.money(row.amount),
        this.money(row.company),
        this.money(row.employee),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
